import { Alert, Box, Modal, styled } from '@mui/material';
import UPButton from 'components/button';
import { MfaCard } from '../mfa-styles';

/** Enrollment-only. The layout both MFA screens share lives in `src/modules/auth/mfa-styles.ts`. */

export const MfaQrFrame = styled(Box)(() => ({
  padding: '12px',
  backgroundColor: '#ffffff',
  borderRadius: '8px',
  lineHeight: 0,
}));

export const MfaSecret = styled(Box)(({ theme }) => ({
  fontFamily: 'monospace',
  fontSize: '15px',
  letterSpacing: '2px',
  wordBreak: 'break-all',
  textAlign: 'center',
  cursor: 'pointer',
  color: theme.palette.secondary.contrastText,
}));

/** Two columns of four, filled top-to-bottom so each column holds four whole codes. */
export const MfaCodesGrid = styled(Box)(() => ({
  display: 'grid',
  gridTemplateRows: 'repeat(4, auto)',
  gridAutoFlow: 'column',
  columnGap: '24px',
  rowGap: '8px',
  justifyContent: 'center',
}));

export const MfaCode = styled(Box)(({ theme }) => ({
  fontFamily: 'monospace',
  fontSize: '16px',
  letterSpacing: '1px',
  color: theme.palette.secondary.contrastText,
}));

export const MfaCodesActions = styled(Box)(() => ({
  display: 'flex',
  gap: '10px',
  justifyContent: 'center',
}));

/* ── Grace-period opt-in: banner + modal (US 93) ──────────────────────────── */

/**
 * Same geometry as `delete-modal` / `general-confirm-modal` so the opt-in dialog sits where every
 * other modal in the app does. Above the app's own `Backdrop` (z-index 9000) for the same reason
 * those two are: the request spinner must not paint over the QR code.
 */
export const MfaEnrollmentModalStyled = styled(Modal)(({ theme }) => ({
  position: 'fixed',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '550px',
  zIndex: '9500',
  height: 'auto',
  // Collapses the Modal's own full-viewport backdrop, exactly as `delete-modal` does. Without it the
  // dialog dims the page twice: once through the Backdrop wrapping it, once through this one.
  '.MuiBackdrop-root': {
    boxShadow: '0px 0px 8px rgba(0, 0, 0, 0.05)',
    borderRadius: '8px',
    position: 'relative',
  },
  [theme.breakpoints.down(611)]: {
    width: '90%',
  },
}));

export const MfaEnrollmentModalContainer = styled(Box)(() => ({
  width: '100%',
  background: 'white',
  padding: '32px',
  boxShadow: '0px 0px 8px rgba(0, 0, 0, 0.05)',
  borderRadius: '8px',
  border: 'none',
  maxHeight: '80vh',
  overflowY: 'auto',
}));

/** `MfaCard` is `marginLeft: auto`-positioned by its page column; inside a modal it centres itself. */
export const MfaModalCard = styled(MfaCard)(() => ({
  margin: '0 auto',
}));

export const MfaBannerStyled = styled(Alert)(() => ({
  color: '#FF981F',
  background: '#FFF4E5',
  minHeight: '48px',
  marginBottom: '24px',
  alignItems: 'center',
  '.MuiAlert-message': {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '16px',
    width: '100%',
  },
  '.MuiAlert-action': {
    padding: 0,
    marginRight: 0,
    alignItems: 'center',
  },
}));

export const MfaBannerMessage = styled(Box)(() => ({
  flex: '1 1 auto',
  minWidth: '200px',
}));

/** Fixed to its content so the CTA does not stretch to the banner's full width on a narrow screen. */
export const MfaBannerCta = styled(UPButton)(() => ({
  flex: '0 0 auto',
  whiteSpace: 'nowrap',
}));

export const MfaBannerError = styled(Box)(({ theme }) => ({
  flex: '1 1 100%',
  fontSize: '13px',
  color: theme.palette.error.main,
}));
